import React, { useMemo } from "react";
import { type NextPage } from "next";
import Image from "next/image";
import { Box, Flex, SimpleGrid } from "@chakra-ui/react";
import { trackEvent } from "lib/analytics";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { VideoFull } from "components/video-full";
import { StackedLogos } from "components/logos";
import { Link } from "ui/link";
import { AuraPlate } from "ui/plates";
import { Card } from "ui/cards";
import { Button } from "ui/button";
import { TextTitle } from "ui/text";
import Balancer from "react-wrap-balancer";
import { Diptych, DiptychColumn } from "ui/diptych";
import { LegacyMediaHero } from "components/hero/legacy-media-hero";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/dbt/hero.svg";
import firstPairImage from "../../../public/assets/solutions/dbt/pair-1.svg";
import secondPairImage from "../../../public/assets/solutions/dbt/pair-2.svg";
import thirdPairImage from "../../../public/assets/solutions/dbt/pair-3.svg";

const featuredCaseStudies = [
  {
    customerSlug: "ramp",
    title:
      "Ramp automates 25% sales pipeline through personalized customer outreach using Snowflake, dbt, and Hightouch",
  },
  {
    customerSlug: "lucidchart",
    title: "Lucid activates purpose-built dbt models to marketing and ad teams and increases ROAS by 52%",
  },
  {
    customerSlug: "circleci",
    title: "CircleCI activates dbt product-usage data models for better churn prediction and pursing targeted PQLs",
  },
];

const Dbt: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos, defaultMobileLogos, customers }) => {
  const caseStudyCards = useMemo(() => {
    return featuredCaseStudies.map((caseStudy) => {
      const customer = getSafeCustomer(customers, caseStudy.customerSlug);

      return (
        <Card
          key={customer.slug}
          variant="shadow"
          href={`/customers/${customer.slug}`}
          title={caseStudy.title}
          mediaRatio={2 / 1}
          actions={
            <Button hasRightChevron as="p">
              Read customer story
            </Button>
          }
          media={
            <Flex bg={customer.slug} pos="relative" justify="center" align="center" width="100%">
              <Box pos="absolute" filter="brightness(0) invert(1)" zIndex={1} width="50%">
                <Image
                  src={`${customer.logoMonoUrl ?? customer.logoUrl}?w=400`}
                  alt={customer.name + "."}
                  width={200}
                  height={40}
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "auto",
                  }}
                />
              </Box>
            </Flex>
          }
          mb="bigger"
        />
      );
    });
  }, [customers]);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.green.150"
    >
      <LegacyMediaHero
        variant="green"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        imageSrc={heroImage}
        imageAlt="Hightouch and dbt logos."
      />

      <StackedLogos customers={defaultLogos} mobileCustomers={defaultMobileLogos} my="0" pt="small" />

      <VideoFull
        source="https://cdn.sanity.io/files/pwmfmi47/production/28b600cb8a0e813b85206e5fbd2f44db9ebac380.mp4"
        poster="https://cdn.sanity.io/images/pwmfmi47/production/30fa52a12054367ea83d598a9d61bf5d9b397d52-5120x2880.webp"
        title="Watch Maria walk through our various dbt integrations"
        description="Learn about Hightouch's various dbt integrations in less than 5 minutes."
        duration="5 mins"
        onPlay={() => {
          trackEvent("Dbt Video Played");
        }}
        onEnded={() => {
          trackEvent("Dbt Video Ended");
        }}
      />

      <Section>
        <TextTitle
          size="lg"
          align="center"
          title={<Balancer>Activate your transformed data with Hightouch + dbt Labs</Balancer>}
          maxW="3xl"
        />

        <DiptychColumn mt={{ base: "biggest", md: "huge" }}>
          <Diptych
            direction="left"
            mediaRatio={443 / 337}
            media={
              <Box p="small">
                <Image src={firstPairImage} alt="" />
              </Box>
            }
            title="Build your model once, run it anywhere"
            description={`Building models should be the hardest part of your job. Make sure they get actioned by syncing
                transformed data into ${integrationsCount}+ downstream business tools. Rest assured that any changes you
                make to your models are automatically reflected in your Hightouch syncs. You can also define and govern
                your Hightouch models straight from your dbt repo.`}
            actions={
              <Button hasRightChevron href="/docs/extensions/dbt-models" target="_blank">
                Get started
              </Button>
            }
          />

          <Diptych
            direction="right"
            mediaRatio={366 / 343}
            media={
              <Box p="small">
                <Image src={secondPairImage} alt="" />
              </Box>
            }
            title="Sync data as soon as it’s available"
            description="Automate your workflows and trigger your Hightouch syncs and sync sequences to run immediately after your dbt Cloud jobs complete. That’s one less thing for your data teams to manage and ensures downstream teams are making decisions based on the freshest data."
            actions={
              <Button hasRightChevron href="/docs/extensions/dbt-cloud" target="_blank">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={599 / 231}
            media={
              <Box p="small">
                <Image src={thirdPairImage} alt="" />
              </Box>
            }
            title="Get detailed lineage and peace of mind"
            description={
              <>
                Dependencies abound in the modern data stack. With{" "}
                <Link variant="inlineDark" href="/blog/dbt-code-checks">
                  dbt CI checks
                </Link>{" "}
                and our native{" "}
                <Link variant="inlineDark" href="/blog/introducing-dbt-exposures-for-hightouch">
                  dbt Exposures
                </Link>{" "}
                integration, you get the visibility and guardrails needed to ensure that the changes you’re making to
                your dbt models don’t break your downstream syncs.
              </>
            }
            actions={
              <Button hasRightChevron href="/playbooks/dbt-pipeline-observability">
                Read the playbook
              </Button>
            }
          />
        </DiptychColumn>
      </Section>

      <AuraPlate condensed variant="green">
        <CenteredQuote
          maxW="4xl"
          quoteText="Thanks to Hightouch’s Git Sync, dbt Exposures, and dbt CI checks features, we’re able to avoid sync outages by knowing exactly what will happen to our models and syncs before we make changes to the upstream entities and systems they depend on."
          author={{ name: "Schylar Brock", job: "Senior Analytics Engineering Lead" }}
          company={{
            name: "Vendr",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/eaffd1ff20eacedf96184b2c461c5ef67c8c1b2e-500x125.png",
          }}
          cta={{ href: "/customers/vendr", text: "Read customer story" }}
        />
      </AuraPlate>

      <Section my={0} py={sectionSpacingY}>
        <TextTitle
          size="lg"
          align="center"
          title={<Balancer>Read how our joint customers are putting their dbt models to work with Hightouch</Balancer>}
          maxW="5xl"
          mb={{ base: "bigger", md: "biggest" }}
        />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} columnGap="small" justifyItems="center">
          {caseStudyCards}
        </SimpleGrid>
      </Section>
    </Page>
  );
};

export default Dbt;

export const getStaticProps = getLandingPageProps("/solutions/dbt", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
  loadCustomers: featuredCaseStudies.map((caseStudy) => caseStudy.customerSlug),
});
